import {
  type MatchDashboardApplicantFilters,
  type MatchDashboardGigListFilters,
  matchDashboardCandidatesInfiniteQueryOptions,
  matchDashboardFilterOptionsQueryOptions,
  matchDashboardGigDetailQueryOptions,
  matchDashboardGigListQueryOptions,
  matchDashboardGrantedProsInfiniteQueryOptions,
  matchDashboardSummaryQueryOptions,
  matchDashboardWaitlistedProsInfiniteQueryOptions,
} from "api/queries/matchDashboard";
import type { commandCenter } from "routing/tree/admin";
import type { WarmableQuery } from "routing/warmQueries";

export const GIG_LIST_PAGE_SIZE = 50;
const CANDIDATES_PAGE_SIZE = 200;

type QueriesInput = { search: Omit<ReturnType<typeof commandCenter.useSearch>, "c_at_risk"> };

export const toGigFilters = ({ search }: QueriesInput): MatchDashboardGigListFilters => ({
  status: search.status,
  location_id: search.location_id,
  market_id: search.market_id.length > 0 ? search.market_id : undefined,
  skill_id: search.skill_id.length > 0 ? search.skill_id : undefined,
  gig_type: search.gig_type,
  auto_select: search.auto_select,
  upcoming_days: search.upcoming_days,
  search: search.search,
  fill_status: search.fill_status,
  needs_attention: search.needs_attention,
  is_vip: search.is_vip,
  is_first_5_for_location: search.is_first_5_for_location,
});

export const toCandidateFilters = ({ search }: QueriesInput): MatchDashboardApplicantFilters => ({
  selection_source: search.c_source,
  type: search.c_type,
  status: search.c_status,
  ordering: search.c_ordering,
  min_score: search.c_min_score,
  max_distance: search.c_max_distance,
  min_rating: search.c_min_rating,
  min_paid_gigs: search.c_min_paid_gigs,
  min_cross_coef: search.c_min_cross_coef,
  has_push_token: search.c_has_push_token,
});

export const filterOptionsQuery = () => matchDashboardFilterOptionsQueryOptions();

export const summaryQuery = () => matchDashboardSummaryQueryOptions();

export const gigListQuery = (input: QueriesInput) =>
  matchDashboardGigListQueryOptions({
    ...toGigFilters(input),
    page: input.search.page,
    page_size: GIG_LIST_PAGE_SIZE,
  });

export const gigDetailQuery = ({ search }: QueriesInput) => matchDashboardGigDetailQueryOptions(search.gig_id ?? null);

export const candidatesQuery = (input: QueriesInput) =>
  matchDashboardCandidatesInfiniteQueryOptions(input.search.gig_id ?? null, {
    page_size: CANDIDATES_PAGE_SIZE,
    ...toCandidateFilters(input),
  });

/** Not warmed: it only runs once the gig detail says the shift has granted Pros. */
export const grantedProsQuery = (gigId: number, filters: MatchDashboardApplicantFilters) =>
  matchDashboardGrantedProsInfiniteQueryOptions(gigId, { page_size: CANDIDATES_PAGE_SIZE, ...filters });

/** Not warmed: it only runs once the gig detail shows the shift has a waitlist. */
export const waitlistedProsQuery = (gigId: number) =>
  matchDashboardWaitlistedProsInfiniteQueryOptions(gigId, CANDIDATES_PAGE_SIZE);

export const queries = (input: QueriesInput): Array<WarmableQuery> => [
  filterOptionsQuery(),
  summaryQuery(),
  gigListQuery(input),
  gigDetailQuery(input),
  candidatesQuery(input),
];
